import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { Link } from 'react-router-dom';
import { comfortProducts, mobilityProducts, supplementProducts } from '../../../mocks/affiliate-products';
import AffiliateDisclosure from '../../../components/base/AffiliateDisclosure';
import SEOHead from '../../../components/base/SEOHead';

export default function ArthritisGuidePage() {
  const relatedArticles = [
    {
      title: 'Das Zuhause seniorengerecht gestalten',
      excerpt: 'Praktische Anpassungen für mehr Sicherheit.',
      link: '/ratgeber/zuhause-seniorengerecht',
      image: 'https://public.readdy.ai/ai/img_res/8ee60f76a855c56d19984e9fd65294ab.jpg'
    },
    {
      title: 'Typische Anzeichen des Alterns bei Hunden',
      excerpt: 'Erkennen Sie die ersten Zeichen des Älterwerdens.',
      link: '/ratgeber/altersanzeichen',
      image: 'https://readdy.ai/api/search-image?query=Senior%20golden%20retriever%20dog%20with%20gray%20muzzle%20resting%20peacefully%20on%20soft%20blanket%20warm%20natural%20lighting%20gentle%20expression%20showing%20signs%20of%20aging%20comfortable%20home%20environment%20soft%20earth%20tones&width=400&height=250&seq=aging-signs-rel-1&orientation=landscape'
    },
    {
      title: 'Inkontinenz beim alten Hund: Was Sie wissen sollten',
      excerpt: 'Ursachen, Management-Strategien und würdevolle Lösungen.',
      link: '/ratgeber/inkontinenz',
      image: 'https://readdy.ai/api/search-image?query=Senior%20dog%20resting%20comfortably%20on%20washable%20waterproof%20bed%20pad%20in%20cozy%20home%20setting%20warm%20natural%20lighting%20dignified%20elderly%20dog%20care%20soft%20earth%20tones%20beige%20and%20cream%20colors%20peaceful%20atmosphere%20clean%20hygienic%20environment&width=400&height=250&seq=incontinence-rel-1&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-sand">
      <SEOHead 
        title="Arthrose beim Hund erkennen und unterstützen | Hund im Alter"
        description="Arthrose beim Hund: Symptome erkennen, Behandlung und praktische Tipps für mehr Lebensqualität bei Gelenkproblemen."
        keywords="Arthrose Hund, Gelenkprobleme Hund, Hund steif, Arthrose Behandlung Hund"
      />
      <Header />
      <main>
        {/* Hero Section */}
        <div className="relative h-[500px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Senior%20dog%20with%20arthritis%20being%20gently%20supported%20while%20walking%20showing%20careful%20movement%20and%20joint%20stiffness%20warm%20home%20interior%20natural%20lighting%20realistic%20photography%20showing%20full%20body%20dignified%20elderly%20dog%20care%20soft%20earth%20tones%20beige%20and%20cream%20colors%20peaceful%20atmosphere&width=1920&height=500&seq=arthritis-hero-1&orientation=landscape"
            alt="Arthrose beim Hund erkennen und unterstützen"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20"></div>
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-4xl mx-auto px-6 lg:px-8 pb-16 w-full">
              <nav className="text-sm text-white/80 mb-4">
                <Link to="/" className="hover:text-white">Start</Link>
                <span className="mx-2">/</span>
                <Link to="/ratgeber" className="hover:text-white">Ratgeber</Link>
                <span className="mx-2">/</span>
                <span>Arthrose</span>
              </nav>
              <span className="inline-block px-3 py-1 bg-teal text-white text-sm font-medium rounded-full mb-4">
                Gesundheit & Mobilität
              </span>
              <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-4">
                Arthrose beim Hund: Erkennen, verstehen und unterstützen
              </h1>
              <p className="text-xl text-white/90 leading-relaxed max-w-2xl">
                Wie Sie Gelenkprobleme frühzeitig erkennen und Ihrem Hund mit praktischen Maßnahmen zu mehr Lebensqualität verhelfen.
              </p>
            </div>
          </div>
        </div>

        {/* Article Content */}
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
          {/* Author & Date */}
          <div className="flex items-center gap-4 mb-12 pb-8 border-b border-gray-200">
            <div className="w-12 h-12 rounded-full bg-teal/20 flex items-center justify-center">
              <i className="ri-user-heart-line text-teal text-xl"></i>
            </div>
            <div>
              <p className="text-sm font-medium text-navy">Hund-im-Alter Redaktion</p>
              <p className="text-sm text-gray-500">Aktualisiert: Januar 2025 · 12 Min. Lesezeit</p>
            </div>
          </div>

          {/* Introduction */}
          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-xl text-gray-700 leading-relaxed">
              Wenn Ihr Hund morgens steif aufsteht, beim Treppensteigen zögert oder nach Spaziergängen länger braucht, um sich zu erholen, könnte Arthrose dahinterstecken. Diese Gelenkerkrankung ist bei älteren Hunden sehr häufig – und obwohl sie nicht heilbar ist, gibt es viele Möglichkeiten, Ihrem Hund das Leben zu erleichtern und <strong>Schmerzen zu lindern</strong>.
            </p>
          </div>

          {/* Table of Contents */}
          <div className="bg-white rounded-2xl p-8 mb-12">
            <h2 className="text-xl font-bold text-navy mb-4 flex items-center gap-2">
              <i className="ri-list-check text-teal"></i>
              Inhalt dieses Artikels
            </h2>
            <ul className="space-y-2">
              <li>
                <a href="#was-ist-arthrose" className="text-teal hover:underline cursor-pointer">1. Was ist Arthrose?</a>
              </li>
              <li>
                <a href="#ursachen" className="text-teal hover:underline cursor-pointer">2. Ursachen und Risikofaktoren</a>
              </li>
              <li>
                <a href="#symptome" className="text-teal hover:underline cursor-pointer">3. Typische Symptome erkennen</a>
              </li>
              <li>
                <a href="#diagnose" className="text-teal hover:underline cursor-pointer">4. Diagnose beim Tierarzt</a>
              </li>
              <li>
                <a href="#behandlung" className="text-teal hover:underline cursor-pointer">5. Behandlungsmöglichkeiten</a>
              </li>
              <li>
                <a href="#alltag" className="text-teal hover:underline cursor-pointer">6. Unterstützung im Alltag</a>
              </li>
              <li>
                <a href="#bewegung" className="text-teal hover:underline cursor-pointer">7. Die richtige Bewegung</a>
              </li>
              <li>
                <a href="#ernaehrung" className="text-teal hover:underline cursor-pointer">8. Ernährung und Gewichtsmanagement</a>
              </li>
            </ul>
          </div>

          {/* Section 1 */}
          <section id="was-ist-arthrose" className="mb-16">
            <h2 className="text-3xl font-bold text-navy mb-6">
              1. Was ist Arthrose?
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Arthrose (auch Osteoarthrose genannt) ist eine <strong>degenerative Gelenkerkrankung</strong>, bei der der Knorpel in den Gelenken nach und nach abgebaut wird. Dieser Knorpel dient normalerweise als Puffer zwischen den Knochen und ermöglicht reibungslose Bewegungen.
            </p>
            <div className="bg-teal/10 rounded-xl p-6 mb-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className="ri-information-line text-teal text-2xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-navy mb-2">Was passiert bei Arthrose?</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-teal mt-1">•</span>
                      <span>Der Knorpel wird dünner und rauer</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-teal mt-1">•</span>
                      <span>Die Knochen reiben aneinander</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-teal mt-1">•</span>
                      <span>Das Gelenk entzündet sich</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-teal mt-1">•</span>
                      <span>Es entstehen Schmerzen und Steifheit</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-teal mt-1">•</span>
                      <span>Die Beweglichkeit nimmt ab</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <p className="text-lg text-gray-700 leading-relaxed">
              Arthrose ist <strong>nicht heilbar</strong>, aber mit der richtigen Behandlung und Unterstützung können die Symptome deutlich gelindert werden. Viele Hunde mit Arthrose können noch Jahre lang ein gutes Leben führen.
            </p>
          </section>

          {/* Section 2 */}
          <section id="ursachen" className="mb-16">
            <h2 className="text-3xl font-bold text-navy mb-6">
              2. Ursachen und Risikofaktoren
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              Arthrose kann verschiedene Ursachen haben. Oft spielen mehrere Faktoren zusammen:
            </p>

            <div className="space-y-6">
              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-teal text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-navy mb-2">Alter und natürlicher Verschleiß</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Mit zunehmendem Alter nutzt sich der Knorpel ab – das ist ein normaler Prozess. Je älter der Hund, desto höher das Arthrose-Risiko. Besonders betroffen sind Hunde ab 8 Jahren.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-scales-3-line text-teal text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-navy mb-2">Übergewicht</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Jedes zusätzliche Kilogramm belastet die Gelenke. Übergewichtige Hunde haben ein deutlich höheres Risiko für Arthrose und stärkere Beschwerden.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-dna-line text-teal text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-navy mb-2">Genetische Veranlagung</h3>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      Manche Rassen sind anfälliger für Gelenkprobleme, besonders große und schwere Rassen wie:
                    </p>
                    <ul className="space-y-1 text-gray-700 ml-4">
                      <li className="flex items-center gap-2">
                        <span className="text-teal">•</span>
                        <span>Deutscher Schäferhund</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-teal">•</span>
                        <span>Labrador Retriever</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-teal">•</span>
                        <span>Golden Retriever</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-teal">•</span>
                        <span>Rottweiler</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-teal">•</span>
                        <span>Berner Sennenhund</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-first-aid-kit-line text-teal text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-navy mb-2">Verletzungen und Fehlstellungen</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Frühere Verletzungen (Kreuzbandriss, Knochenbrüche), Hüftdysplasie oder andere Gelenkfehlstellungen erhöhen das Arthrose-Risiko erheblich.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-run-line text-teal text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-navy mb-2">Überlastung</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Zu intensive Bewegung, Hundesport oder häufiges Springen (z. B. Agility) können die Gelenke vorzeitig verschleißen – besonders bei jungen, noch wachsenden Hunden.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section id="symptome" className="mb-16">
            <h2 className="text-3xl font-bold text-navy mb-6">
              3. Typische Symptome erkennen
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              Arthrose entwickelt sich schleichend. Viele Hundebesitzer bemerken die ersten Anzeichen erst spät, weil Hunde Schmerzen oft lange verbergen. Achten Sie auf diese Warnsignale:
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white rounded-xl p-6">
                <h3 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
                  <i className="ri-walk-line text-teal"></i>
                  Bewegungsveränderungen
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal mt-1"></i>
                    <span>Steifheit nach dem Aufstehen oder Liegen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal mt-1"></i>
                    <span>Langsames, vorsichtiges Gehen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal mt-1"></i>
                    <span>Hinken oder Schonen eines Beins</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal mt-1"></i>
                    <span>Schwierigkeiten beim Treppensteigen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal mt-1"></i>
                    <span>Vermeidung von Sprüngen (ins Auto, aufs Sofa)</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl p-6">
                <h3 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
                  <i className="ri-emotion-unhappy-line text-teal"></i>
                  Verhaltensänderungen
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal mt-1"></i>
                    <span>Weniger Spielfreude und Aktivität</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal mt-1"></i>
                    <span>Kürzere Spaziergänge gewünscht</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal mt-1"></i>
                    <span>Mehr Ruhe- und Schlafbedarf</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal mt-1"></i>
                    <span>Berührungsempfindlichkeit an bestimmten Stellen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal mt-1"></i>
                    <span>Unruhe oder Winseln beim Hinlegen</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-orange-50 border-l-4 border-orange-400 rounded-xl p-6">
              <h3 className="text-lg font-bold text-navy mb-3 flex items-center gap-2">
                <i className="ri-alert-line text-orange-500"></i>
                Wichtig zu wissen
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Arthrose-Symptome sind oft <strong>wetterabhängig</strong>. An kalten, feuchten Tagen sind die Beschwerden meist stärker. Auch nach Ruhephasen (morgens, nach dem Schlafen) zeigt sich die Steifheit besonders deutlich – sie bessert sich aber oft nach ein paar Schritten.
              </p>
            </div>
          </section>

          {/* Section 4 */}
          <section id="diagnose" className="mb-16">
            <h2 className="text-3xl font-bold text-navy mb-6">
              4. Diagnose beim Tierarzt
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Wenn Sie Arthrose-Symptome bei Ihrem Hund bemerken, sollten Sie zeitnah einen Tierarzt aufsuchen. Je früher die Diagnose, desto besser können Sie gegensteuern.
            </p>

            <div className="bg-white rounded-xl p-6 mb-8">
              <h4 className="font-semibold text-navy mb-4">Was der Tierarzt untersuchen wird:</h4>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <i className="ri-stethoscope-line text-teal text-xl mt-1"></i>
                  <div>
                    <h5 className="font-medium text-navy mb-1">Klinische Untersuchung</h5>
                    <p className="text-gray-700 text-sm">Abtasten der Gelenke, Bewegungstests, Schmerzreaktionen prüfen</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-scan-line text-teal text-xl mt-1"></i>
                  <div>
                    <h5 className="font-medium text-navy mb-1">Röntgenaufnahmen</h5>
                    <p className="text-gray-700 text-sm">Zeigen Knorpelabbau, Knochenveränderungen und Gelenkspalt-Verengung</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-walk-line text-teal text-xl mt-1"></i>
                  <div>
                    <h5 className="font-medium text-navy mb-1">Gangbildanalyse</h5>
                    <p className="text-gray-700 text-sm">Beobachtung, wie Ihr Hund läuft und sich bewegt</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-test-tube-line text-teal text-xl mt-1"></i>
                  <div>
                    <h5 className="font-medium text-navy mb-1">Blutuntersuchung (optional)</h5>
                    <p className="text-gray-700 text-sm">Zum Ausschluss anderer Erkrankungen oder vor Medikamentengabe</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-teal/10 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className="ri-lightbulb-line text-teal text-2xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-navy mb-2">Tipp: Vorbereitung auf den Tierarztbesuch</h4>
                  <p className="text-gray-700 mb-3">
                    Notieren Sie sich vor dem Termin:
                  </p>
                  <ul className="space-y-1 text-gray-700 ml-4">
                    <li className="flex items-center gap-2">
                      <span className="text-teal">•</span>
                      <span>Seit wann zeigt Ihr Hund Symptome?</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-teal">•</span>
                      <span>Welche Bewegungen fallen ihm schwer?</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-teal">•</span>
                      <span>Wann sind die Beschwerden am stärksten?</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-teal">•</span>
                      <span>Gab es frühere Verletzungen?</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Section 5 */}
          <section id="behandlung" className="mb-16">
            <h2 className="text-3xl font-bold text-navy mb-6">
              5. Behandlungsmöglichkeiten
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              Arthrose ist nicht heilbar, aber die Symptome lassen sich gut behandeln. Die Therapie kombiniert meist mehrere Ansätze:
            </p>

            <div className="space-y-8">
              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-navy mb-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-teal/10 rounded-full flex items-center justify-center">
                    <i className="ri-capsule-line text-teal"></i>
                  </div>
                  Schmerzmedikamente
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Entzündungshemmende Schmerzmittel (NSAIDs) sind oft die Basis der Behandlung. Sie lindern Schmerzen und Entzündungen im Gelenk.
                </p>
                <div className="bg-sand rounded-lg p-4">
                  <p className="text-sm text-gray-700">
                    <strong>Wichtig:</strong> Geben Sie Ihrem Hund niemals Schmerzmittel für Menschen (wie Ibuprofen oder Paracetamol) – diese können für Hunde tödlich sein! Nur vom Tierarzt verschriebene Medikamente verwenden.
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-navy mb-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-teal/10 rounded-full flex items-center justify-center">
                    <i className="ri-heart-pulse-line text-teal"></i>
                  </div>
                  Gelenkergänzungen (Nahrungsergänzungsmittel)
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Präparate mit Glucosamin, Chondroitin, MSM oder Grünlippmuschel können den Knorpel unterstützen und Entzündungen reduzieren.
                </p>
                <p className="text-sm text-gray-600 italic">
                  Wirkung: Diese Mittel wirken nicht sofort, sondern entfalten ihre Wirkung über Wochen bis Monate. Sie sind kein Ersatz für Schmerzmittel, können aber langfristig helfen.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-navy mb-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-teal/10 rounded-full flex items-center justify-center">
                    <i className="ri-hand-heart-line text-teal"></i>
                  </div>
                  Physiotherapie
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Gezielte Übungen, Massagen und Bewegungstherapie stärken die Muskulatur, verbessern die Beweglichkeit und lindern Schmerzen.
                </p>
                <ul className="space-y-2 text-gray-700 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="text-teal">•</span>
                    <span>Unterwasserlaufband (Hydrotherapie)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal">•</span>
                    <span>Passive Bewegungsübungen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal">•</span>
                    <span>Wärme- und Kältetherapie</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal">•</span>
                    <span>Elektrotherapie (TENS)</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-navy mb-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-teal/10 rounded-full flex items-center justify-center">
                    <i className="ri-syringe-line text-teal"></i>
                  </div>
                  Injektionen ins Gelenk
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Bei starken Beschwerden können Hyaluronsäure oder Kortison direkt ins Gelenk gespritzt werden. Dies lindert Schmerzen und verbessert die Gelenkschmierung.
                </p>
                <p className="text-sm text-gray-600 italic">
                  Häufigkeit: Meist alle paar Monate, je nach Bedarf
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-navy mb-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-teal/10 rounded-full flex items-center justify-center">
                    <i className="ri-scissors-cut-line text-teal"></i>
                  </div>
                  Operation (in schweren Fällen)
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Bei sehr fortgeschrittener Arthrose kann eine Operation sinnvoll sein – z. B. Gelenkersatz (künstliche Hüfte) oder Gelenkversteifung. Dies ist aber nur in Ausnahmefällen nötig.
                </p>
              </div>
            </div>
          </section>

          {/* Section 6 */}
          <section id="alltag" className="mb-16">
            <h2 className="text-3xl font-bold text-navy mb-6">
              6. Unterstützung im Alltag
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              Neben der medizinischen Behandlung können Sie Ihrem Hund mit einfachen Anpassungen im Alltag viel helfen:
            </p>

            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-teal/10 rounded-full">
                  <i className="ri-hotel-bed-line text-2xl text-teal"></i>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-navy mb-2">Orthopädische Liegeplätze</h4>
                  <p className="text-base text-gray-700 leading-relaxed">
                    Ein gutes Hundebett mit Memory-Schaum entlastet die Gelenke und sorgt für erholsamen Schlaf. Achten Sie auf ausreichende Polsterung (mindestens 10 cm) und einen niedrigen Einstieg.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-teal/10 rounded-full">
                  <i className="ri-footprint-line text-2xl text-teal"></i>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-navy mb-2">Rutschfeste Böden</h4>
                  <p className="text-base text-gray-700 leading-relaxed">
                    Legen Sie rutschfeste Matten auf glatten Böden aus. Das gibt Ihrem Hund mehr Sicherheit und schont die Gelenke beim Aufstehen und Laufen.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-teal/10 rounded-full">
                  <i className="ri-stairs-line text-2xl text-teal"></i>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-navy mb-2">Rampen und Aufstiegshilfen</h4>
                  <p className="text-base text-gray-700 leading-relaxed">
                    Rampen fürs Auto, Sofa oder Bett ersparen Ihrem Hund schmerzhafte Sprünge. Auch Treppenstufen sollten vermieden oder mit Rampen ersetzt werden.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-teal/10 rounded-full">
                  <i className="ri-t-shirt-line text-2xl text-teal"></i>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-navy mb-2">Tragegeschirre</h4>
                  <p className="text-base text-gray-700 leading-relaxed">
                    Spezielle Geschirre mit Griffen helfen Ihnen, Ihren Hund beim Aufstehen, Treppensteigen oder Einsteigen ins Auto zu unterstützen.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-teal/10 rounded-full">
                  <i className="ri-temp-cold-line text-2xl text-teal"></i>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-navy mb-2">Wärme bei kaltem Wetter</h4>
                  <p className="text-base text-gray-700 leading-relaxed">
                    Kälte verschlimmert Arthrose-Beschwerden. Ein wärmender Hundemantel bei Spaziergängen und warme Liegeplätze helfen. Auch Wärmekissen oder -decken können Linderung bringen.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Alltagshilfen Section */}
          <section id="alltagshilfen" className="bg-white rounded-2xl p-10">
            <h2 className="text-3xl font-bold text-navy mb-6">
              Alltagshilfen für Arthrose-Hunde
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Neben medizinischer Behandlung können praktische Hilfsmittel den Alltag Ihres Hundes erheblich erleichtern und die Gelenke entlasten.
            </p>

            <AffiliateDisclosure />

            <div className="space-y-8 mt-8">
              {/* Orthopädisches Hundebett */}
              {comfortProducts.slice(0, 1).map((product) => (
                <div key={product.id} className="bg-sand rounded-2xl p-8">
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="w-full md:w-48 h-48 flex-shrink-0">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover rounded-xl"
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-navy mb-4">
                        {product.name}
                      </h3>
                      <div className="mb-4">
                        <h4 className="text-base font-semibold text-teal mb-2">
                          Warum diese Lösung hilft:
                        </h4>
                        <p className="text-base text-gray-700 leading-relaxed">
                          {product.reason}
                        </p>
                      </div>
                      <div className="mb-6">
                        <h4 className="text-base font-semibold text-navy mb-2">
                          Geeignet für:
                        </h4>
                        <ul className="space-y-2">
                          {product.suitableFor.map((item, idx) => (
                            <li key={idx} className="flex items-center gap-2 text-base text-gray-700">
                              <i className="ri-check-line text-teal"></i>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="flex flex-wrap gap-3">
                        <a
                          href={product.amazonLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-6 py-3 bg-navy text-white rounded-full font-medium hover:bg-navy-dark transition-colors whitespace-nowrap cursor-pointer"
                        >
                          Bei Amazon ansehen
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Rampen */}
              {comfortProducts.slice(2, 4).map((product) => (
                <div key={product.id} className="bg-sand rounded-2xl p-8">
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="w-full md:w-48 h-48 flex-shrink-0">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover rounded-xl"
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-navy mb-4">
                        {product.name}
                      </h3>
                      <div className="mb-4">
                        <h4 className="text-base font-semibold text-teal mb-2">
                          Warum diese Lösung hilft:
                        </h4>
                        <p className="text-base text-gray-700 leading-relaxed">
                          {product.reason}
                        </p>
                      </div>
                      <div className="mb-6">
                        <h4 className="text-base font-semibold text-navy mb-2">
                          Geeignet für:
                        </h4>
                        <ul className="space-y-2">
                          {product.suitableFor.map((item, idx) => (
                            <li key={idx} className="flex items-center gap-2 text-base text-gray-700">
                              <i className="ri-check-line text-teal"></i>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="flex flex-wrap gap-3">
                        <a
                          href={product.amazonLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-6 py-3 bg-navy text-white rounded-full font-medium hover:bg-navy-dark transition-colors whitespace-nowrap cursor-pointer"
                        >
                          Bei Amazon ansehen
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Tragehilfe */}
              {mobilityProducts.slice(0, 1).map((product) => (
                <div key={product.id} className="bg-sand rounded-2xl p-8">
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="w-full md:w-48 h-48 flex-shrink-0">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover rounded-xl"
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-navy mb-4">
                        {product.name}
                      </h3>
                      <div className="mb-4">
                        <h4 className="text-base font-semibold text-teal mb-2">
                          Warum diese Lösung hilft:
                        </h4>
                        <p className="text-base text-gray-700 leading-relaxed">
                          {product.reason}
                        </p>
                      </div>
                      <div className="mb-6">
                        <h4 className="text-base font-semibold text-navy mb-2">
                          Geeignet für:
                        </h4>
                        <ul className="space-y-2">
                          {product.suitableFor.map((item, idx) => (
                            <li key={idx} className="flex items-center gap-2 text-base text-gray-700">
                              <i className="ri-check-line text-teal"></i>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="flex flex-wrap gap-3">
                        <a
                          href={product.amazonLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-6 py-3 bg-navy text-white rounded-full font-medium hover:bg-navy-dark transition-colors whitespace-nowrap cursor-pointer"
                        >
                          Bei Amazon ansehen
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              <div className="mt-8 bg-teal/10 border-l-4 border-teal rounded-xl p-6">
                <h4 className="text-lg font-bold text-navy mb-3 flex items-center gap-2">
                  <i className="ri-lightbulb-line text-teal"></i>
                  Weitere Hilfsmittel
                </h4>
                <p className="text-base text-gray-700 leading-relaxed mb-4">
                  Neben diesen Produkten können auch rutschfeste Matten, erhöhte Futternäpfe und Antirutsch-Socken den Alltag Ihres Arthrose-Hundes erleichtern.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link
                    to="/beduerfnisse/komfort-liegen"
                    className="text-teal hover:underline cursor-pointer font-medium"
                  >
                    Alle Komfort-Produkte ansehen →
                  </Link>
                  <Link
                    to="/beduerfnisse/mobilitaet-aufstehen"
                    className="text-teal hover:underline cursor-pointer font-medium"
                  >
                    Alle Mobilitäts-Produkte ansehen →
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Bewegung Section */}
          <section id="bewegung" className="mb-16">
            <h2 className="text-3xl font-bold text-navy mb-6">
              7. Die richtige Bewegung
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Bewegung ist bei Arthrose wichtig – aber die richtige Dosis macht den Unterschied. Zu viel belastet die Gelenke, zu wenig führt zu Muskelabbau und Steifheit.
            </p>

            <div className="bg-teal/5 border-l-4 border-teal rounded-r-2xl p-6 mb-8">
              <h3 className="text-xl font-bold text-navy mb-3">
                <i className="ri-lightbulb-line text-teal mr-2"></i>
                Die goldene Regel
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                <strong>Lieber mehrere kurze Runden als eine lange Tour.</strong> Regelmäßige, moderate Bewegung hält die Gelenke beweglich und stärkt die Muskulatur – ohne Überlastung.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white border-2 border-teal/20 rounded-2xl p-6">
                <h4 className="text-lg font-semibold text-navy mb-3 flex items-center gap-2">
                  <i className="ri-check-line text-teal text-xl"></i>
                  Empfehlenswert
                </h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-teal">✓</span>
                    <span>Mehrere kurze Spaziergänge (3-4x täglich)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal">✓</span>
                    <span>Langsames, gleichmäßiges Tempo</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal">✓</span>
                    <span>Ebene Wege ohne Steigungen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal">✓</span>
                    <span>Schwimmen (entlastet die Gelenke)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal">✓</span>
                    <span>Sanfte Dehnübungen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal">✓</span>
                    <span>Schnüffelspiele (geistige Auslastung)</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white border-2 border-red-200 rounded-2xl p-6">
                <h4 className="text-lg font-semibold text-navy mb-3 flex items-center gap-2">
                  <i className="ri-close-line text-red-500 text-xl"></i>
                  Vermeiden
                </h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-red-500">✗</span>
                    <span>Lange, anstrengende Wanderungen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500">✗</span>
                    <span>Sprünge (Agility, Frisbee)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500">✗</span>
                    <span>Steile Anstiege oder Treppen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500">✗</span>
                    <span>Wildes Toben mit anderen Hunden</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500">✗</span>
                    <span>Plötzliche Richtungswechsel</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500">✗</span>
                    <span>Bewegung auf hartem Untergrund (Asphalt)</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-sand rounded-xl p-6">
              <h4 className="font-semibold text-navy mb-3 flex items-center gap-2">
                <i className="ri-question-line text-teal"></i>
                Wie viel Bewegung ist richtig?
              </h4>
              <p className="text-gray-700 leading-relaxed">
                Das hängt vom Schweregrad der Arthrose ab. Beobachten Sie Ihren Hund: Wenn er nach dem Spaziergang steifer ist oder hinkt, war es zu viel. Wenn er danach entspannt und zufrieden wirkt, war es genau richtig. Sprechen Sie mit Ihrem Tierarzt über ein individuelles Bewegungsprogramm.
              </p>
            </div>
          </section>

          {/* Section 8 */}
          <section id="ernaehrung" className="mb-16">
            <h2 className="text-3xl font-bold text-navy mb-6">
              8. Ernährung und Gewichtsmanagement
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Das Gewicht Ihres Hundes hat einen <strong>enormen Einfluss</strong> auf die Arthrose-Beschwerden. Jedes Kilogramm zu viel belastet die Gelenke zusätzlich.
            </p>

            <div className="bg-orange-50 border-l-4 border-orange-400 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-bold text-navy mb-3 flex items-center gap-2">
                <i className="ri-scales-3-line text-orange-500"></i>
                Warum Gewichtsreduktion so wichtig ist
              </h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                Studien zeigen: Eine Gewichtsabnahme von nur 6-8% kann die Arthrose-Symptome deutlich verbessern. Bei einem 30 kg schweren Hund sind das gerade mal 2-2,5 kg – aber der Effekt ist enorm.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>Weniger Gewicht = weniger Druck auf die Gelenke = weniger Schmerzen</strong>
              </p>
            </div>

            <div className="space-y-6 mb-12">
              <div className="bg-white rounded-xl p-6">
                <h4 className="text-lg font-semibold text-navy mb-3">Tipps für gesundes Gewichtsmanagement:</h4>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-teal text-xl mt-1"></i>
                    <span><strong>Futtermenge anpassen:</strong> Reduzieren Sie die Portionen schrittweise. Ihr Tierarzt kann Ihnen die optimale Menge berechnen.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-teal text-xl mt-1"></i>
                    <span><strong>Leckerlis einrechnen:</strong> Leckerlis zählen zur Tagesration! Reduzieren Sie die Hauptmahlzeit entsprechend.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-teal text-xl mt-1"></i>
                    <span><strong>Kalorienarme Snacks:</strong> Verwenden Sie Gemüse (Karotten, Gurke) statt Leckerlis.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-teal text-xl mt-1"></i>
                    <span><strong>Regelmäßiges Wiegen:</strong> Kontrollieren Sie das Gewicht alle 2-4 Wochen.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-teal text-xl mt-1"></i>
                    <span><strong>Diätfutter:</strong> Bei starkem Übergewicht kann spezielles Diätfutter helfen.</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl p-6">
                <h4 className="text-lg font-semibold text-navy mb-3">Gelenkunterstützende Nährstoffe:</h4>
                <p className="text-gray-700 mb-4">
                  Manche Nährstoffe können die Gelenkgesundheit unterstützen:
                </p>
                <ul className="space-y-2 text-gray-700 ml-4">
                  <li className="flex items-center gap-2">
                    <span className="text-teal">•</span>
                    <span><strong>Omega-3-Fettsäuren</strong> (aus Fischöl) – entzündungshemmend</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-teal">•</span>
                    <span><strong>Glucosamin & Chondroitin</strong> – unterstützen den Knorpel</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-teal">•</span>
                    <span><strong>Grünlippmuschel</strong> – natürliche Gelenkunterstützung</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-teal">•</span>
                    <span><strong>MSM (Methylsulfonylmethan)</strong> – unterstützt Kollagenbildung</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-teal">•</span>
                    <span><strong>Vitamin E & C</strong> – Antioxidantien</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Nahrungsergänzungsmittel Produktempfehlungen */}
            <div className="bg-white rounded-2xl p-10">
              <h3 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <i className="ri-capsule-line text-teal"></i>
                Empfohlene Gelenkergänzungen
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Diese Nahrungsergänzungsmittel können die Gelenkgesundheit Ihres Hundes unterstützen. Beachten Sie: Die Wirkung setzt meist erst nach mehreren Wochen ein.
              </p>

              <AffiliateDisclosure />

              <div className="space-y-8 mt-8">
                {supplementProducts.map((product) => (
                  <div key={product.id} className="bg-sand rounded-2xl p-8">
                    <div className="flex flex-col md:flex-row gap-6">
                      <div className="w-full md:w-48 h-48 flex-shrink-0">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-cover rounded-xl"
                        />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-2xl font-bold text-navy mb-4">
                          {product.name}
                        </h4>
                        <div className="mb-4">
                          <h5 className="text-base font-semibold text-teal mb-2">
                            Warum diese Ergänzung hilft:
                          </h5>
                          <p className="text-base text-gray-700 leading-relaxed">
                            {product.reason}
                          </p>
                        </div>
                        <div className="mb-6">
                          <h5 className="text-base font-semibold text-navy mb-2">
                            Wirkung:
                          </h5>
                          <ul className="space-y-2">
                            {product.suitableFor.map((item, idx) => (
                              <li key={idx} className="flex items-center gap-2 text-base text-gray-700">
                                <i className="ri-check-line text-teal"></i>
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="flex flex-wrap gap-3">
                          <a
                            href={product.amazonLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-6 py-3 bg-navy text-white rounded-full font-medium hover:bg-navy-dark transition-colors whitespace-nowrap cursor-pointer"
                          >
                            Bei Amazon ansehen
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 bg-teal/10 border-l-4 border-teal rounded-xl p-6">
                <h4 className="text-lg font-bold text-navy mb-3 flex items-center gap-2">
                  <i className="ri-information-line text-teal"></i>
                  Wichtiger Hinweis
                </h4>
                <p className="text-base text-gray-700 leading-relaxed">
                  Nahrungsergänzungsmittel sind <strong>kein Ersatz</strong> für tierärztliche Behandlung oder Schmerzmittel. Sie können die Gelenkgesundheit unterstützen, aber bei akuten Schmerzen ist immer ein Tierarztbesuch nötig. Besprechen Sie die Gabe von Ergänzungsmitteln am besten mit Ihrem Tierarzt.
                </p>
              </div>
            </div>
          </section>

          {/* Checklist */}
          <section className="bg-white rounded-2xl p-8 mb-16">
            <h2 className="text-2xl font-bold text-navy mb-6 flex items-center gap-3">
              <i className="ri-checkbox-multiple-line text-teal"></i>
              Checkliste: Leben mit Arthrose
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-navy mb-3">Medizinisch:</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center gap-2">
                    <i className="ri-checkbox-blank-line text-gray-400"></i>
                    <span>Regelmäßige Tierarztkontrollen</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-checkbox-blank-line text-gray-400"></i>
                    <span>Schmerzmittel nach Bedarf</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-checkbox-blank-line text-gray-400"></i>
                    <span>Gelenkergänzungen geben</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-checkbox-blank-line text-gray-400"></i>
                    <span>Physiotherapie erwägen</span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-navy mb-3">Alltag:</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center gap-2">
                    <i className="ri-checkbox-blank-line text-gray-400"></i>
                    <span>Orthopädisches Hundebett</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-checkbox-blank-line text-gray-400"></i>
                    <span>Rutschfeste Matten auslegen</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-checkbox-blank-line text-gray-400"></i>
                    <span>Rampen für Auto/Sofa</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-checkbox-blank-line text-gray-400"></i>
                    <span>Gewicht kontrollieren</span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-navy mb-3">Bewegung:</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center gap-2">
                    <i className="ri-checkbox-blank-line text-gray-400"></i>
                    <span>Mehrere kurze Spaziergänge</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-checkbox-blank-line text-gray-400"></i>
                    <span>Langsames Tempo</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-checkbox-blank-line text-gray-400"></i>
                    <span>Schwimmen als Therapie</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-checkbox-blank-line text-gray-400"></i>
                    <span>Sprünge vermeiden</span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-navy mb-3">Ernährung:</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center gap-2">
                    <i className="ri-checkbox-blank-line text-gray-400"></i>
                    <span>Idealgewicht halten</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-checkbox-blank-line text-gray-400"></i>
                    <span>Omega-3-Fettsäuren ergänzen</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-checkbox-blank-line text-gray-400"></i>
                    <span>Leckerlis reduzieren</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-checkbox-blank-line text-gray-400"></i>
                    <span>Regelmäßig wiegen</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Conclusion */}
          <section className="mb-16">
            <div className="bg-sand rounded-xl p-8">
              <h2 className="text-2xl font-bold text-navy mb-4">
                Fazit: Ein gutes Leben trotz Arthrose
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Arthrose ist eine chronische Erkrankung, die nicht geheilt werden kann. Aber mit der richtigen Kombination aus medizinischer Behandlung, Alltagsanpassungen und liebevoller Fürsorge können Sie Ihrem Hund noch viele gute Jahre ermöglichen.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                <strong>Wichtig ist:</strong> Beobachten Sie Ihren Hund aufmerksam, reagieren Sie früh auf Veränderungen und arbeiten Sie eng mit Ihrem Tierarzt zusammen. Jeder Hund ist anders – finden Sie gemeinsam heraus, was Ihrem Hund am besten hilft.
              </p>
            </div>
          </section>

          {/* Related Articles */}
          <section>
            <h2 className="text-2xl font-bold text-navy mb-8">
              Weitere hilfreiche Artikel
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedArticles.map((article, index) => (
                <Link
                  key={index}
                  to={article.link}
                  className="group bg-white rounded-xl overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                >
                  <div className="h-40 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="font-semibold text-navy mb-2 group-hover:text-teal transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {article.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
